import { Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { AddIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type {
	MerchandiserFilters,
	MerchandiserListing,
} from '@apple/features/merchandiser/models/listing';
import { getMerchandiserDataTableQueryOptions } from '@apple/features/merchandiser/queries/odata';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/merchandisers')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission:
				'AppleBrandedPrograms.Security.Features.AppleMerchandisersManagement.Read',
		});
	},
	component: MerchandiserManageRoute,
});

function MerchandiserManageRoute() {
	const { t } = useTranslation('manage');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();

	const tableState = useTableState<MerchandiserListing, MerchandiserFilters>({
		search,
		navigate,
		defaultFilters: {},
		defaultSorting: [{ id: 'merchandiserName', desc: true }],
		fieldMap: [],
	});

	const [merchandisersQuery, countriesQuery] = useQueries({
		queries: [
			getMerchandiserDataTableQueryOptions({
				filters: tableState.currentFilterData,
				pagination: tableState.state.pagination,
				commonFilters: {},
			}),
			globalizationQueryOptions.countries,
		],
	});

	const table = useTable({
		features,
		data: merchandisersQuery.data.rows,
		rowCount: merchandisersQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		meta: {
			getFacetedUniqueValues: columnId => {
				switch (columnId) {
					case 'countryName':
						return new Map(countriesQuery.data?.map(x => [x.value, 1]));
					default:
						return undefined;
				}
			},
		},
		columnResizeMode: 'onChange',
		columns: [
			{
				id: 'merchandiserName',
				accessorKey: 'merchandiserName',
				header: t('merchandisers.fields.merchandiserName'),
				filter: {
					group: t('merchandisers.filterGroups.details'),
				},
				cell: ({ row }) => (
					<Link
						to='/manage/merchandisers/$locationId'
						state={{ from: location }}
						params={{ locationId: row.original.id }}
						title={t('merchandisers.links.view')}
					>
						<Text size='sm'>{row.original.merchandiserName}</Text>
					</Link>
				),
			},
			{
				id: 'appleId',
				accessorKey: 'appleId',
				header: t('merchandisers.fields.appleId'),
				size: 450,
				filter: {
					group: t('merchandisers.filterGroups.details'),
				},
			},
			{
				accessorKey: 'countryName',
				header: t('merchandisers.fields.countryName'),
				size: 100,
				filter: {
					group: t('merchandisers.filterGroups.details'),
					variant: 'select',
					getFilterDisplayValue: value =>
						countriesQuery.data?.find(x => x.key === value)?.value ?? String(value),
				},
			},
		],
	});

	return (
		<TableLayout
			table={table}
			title={t('merchandisers.title')}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('merchandisers.buttons.add.tooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/manage/merchandisers/add',
							state: { from: location },
						})
					}
					data-testid='add-merchandiser'
				/>,
			]}
		>
			<DataTable table={table} loading={merchandisersQuery.isFetching} />
		</TableLayout>
	);
}
